import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Flex,
    LoadingOverlay,
    Paper,
    Table,
    Text,
    Title,
    useMantineColorScheme,
    useMantineTheme,
} from "@mantine/core";
import { checkSupportedLocale } from "src/utils/helpers";

import { RequestTenantCreation } from "../../components/RequestTenantCreation";
import { useAuthContext } from "../../hooks/auth";
import { useClientTenants } from "../../hooks/clients";

export interface TenantsPageProps {}
export const TenantsPage = ({}: TenantsPageProps) => {
    const { t } = useTranslation(["common"]);

    const navigate = useNavigate();

    const { colorScheme } = useMantineColorScheme();
    const theme = useMantineTheme();

    const { client } = useAuthContext();

    const { clientTenants, isLoadingClientTenants } = useClientTenants(client?.id);

    return (
        <>
            <Flex align="center" justify="space-between" mb="lg" pb="md" style={{ borderBottom: "1px solid #E0E0E0" }}>
                <Title order={3} style={{ fontWeight: 600 }}>
                    {t("tenants")}
                </Title>
            </Flex>
            {!isLoadingClientTenants && !clientTenants?.length ? (
                <RequestTenantCreation />
            ) : (
                <Paper pos="relative" p={36} radius={12}>
                    <LoadingOverlay visible={isLoadingClientTenants} />
                    <Table verticalSpacing={20} horizontalSpacing={0}>
                        <thead>
                            <tr>
                                <th>
                                    <Text size={14} weight={600} color={colorScheme === "dark" ? "gray.3" : "dark"}>
                                        {t("tenant_name")}
                                    </Text>
                                </th>
                                <th>
                                    <Text size={14} weight={600} color={colorScheme === "dark" ? "gray.3" : "dark"}>
                                        {t("tenant_domain")}
                                    </Text>
                                </th>
                                <th>
                                    <Text size={14} weight={600} color={colorScheme === "dark" ? "gray.3" : "dark"}>
                                        {t("tenant_type")}
                                    </Text>
                                </th>
                                <th>
                                    <Text size={14} weight={600} color={colorScheme === "dark" ? "gray.3" : "dark"}>
                                        {t("tenant_locale")}
                                    </Text>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {(clientTenants || []).map((tenant, index) => (
                                <Box
                                    key={`${index}${tenant?.id}`}
                                    component="tr"
                                    style={{ cursor: "pointer" }}
                                    sx={{
                                        "&:hover": {
                                            backgroundColor:
                                                colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[0],
                                        },
                                    }}
                                    onClick={() => navigate(`/tenant/${tenant?.id}`)}
                                >
                                    <Box
                                        component="td"
                                        style={{ paddingLeft: theme.spacing.xs, paddingRight: theme.spacing.xs }}
                                    >
                                        {tenant?.name}
                                    </Box>
                                    <Box
                                        component="td"
                                        style={{ paddingLeft: theme.spacing.xs, paddingRight: theme.spacing.xs }}
                                    >
                                        {tenant?.domainName}
                                    </Box>
                                    <Box
                                        component="td"
                                        style={{ paddingLeft: theme.spacing.xs, paddingRight: theme.spacing.xs }}
                                    >
                                        {t(`tenant_type_${tenant?.type}`)}
                                    </Box>
                                    <Box
                                        component="td"
                                        style={{ paddingLeft: theme.spacing.xs, paddingRight: theme.spacing.xs }}
                                    >
                                        {checkSupportedLocale(tenant?.locale)
                                            ? t(`lang_${tenant?.locale}`)
                                            : tenant?.locale}
                                    </Box>
                                </Box>
                            ))}
                        </tbody>
                    </Table>
                </Paper>
            )}
        </>
    );
};
